import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Select,
  MenuItem, FormControl, InputLabel, Chip, Pagination, Alert,
  Snackbar, CircularProgress, Grid, Card, CardContent, Divider,
  RadioGroup, FormControlLabel, Radio, Tooltip, Stepper, Step,
  StepLabel, LinearProgress, Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  ContentCopy as CopyIcon,
  FilterList as FilterIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { getApiUrl } from '../../config/api';
import Layout from '../Layout';

const API_URL = getApiUrl();

const MESES = [
  { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' }, { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' }, { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' },
];
const ANOS = [2024, 2025, 2026, 2027];

const CHIP_TIPO_META = {
  valor: { label: 'Valor', color: 'default' },
  quantidade: { label: 'Quantidade', color: 'warning' },
};

const fmt = (v) =>
  v !== null && v !== undefined && v > 0
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const MetasPanel = () => {
  const agora = new Date();
  const [metas, setMetas] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [seguradoras, setSeguradoras] = useState([]);
  const [paginacao, setPaginacao] = useState({ pagina_atual: 1, total_paginas: 1, total_registros: 0 });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filtros
  const [filtros, setFiltros] = useState({
    mes: agora.getMonth() + 1,
    ano: agora.getFullYear(),
    responsavel: '',
    seguradora: '',
    tipo_meta: '',
  });
  const [ordenacao, setOrdenacao] = useState({ order_by: 'competencia', order_direction: 'desc' });

  // Modal CRUD
  const [dialogAberto, setDialogAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({
    id_Responsavel: '', id_seguradora: '', mes: agora.getMonth() + 1,
    ano: agora.getFullYear(), valor: '', quantidade: '',
  });
  const [formErro, setFormErro] = useState('');

  // Modal Copiar Mês
  const [dialogCopiar, setDialogCopiar] = useState(false);
  const [copiarForm, setCopiarForm] = useState({
    mes_origem: agora.getMonth() + 1, ano_origem: agora.getFullYear(),
    mes_destino: agora.getMonth() === 11 ? 1 : agora.getMonth() + 2,
    ano_destino: agora.getMonth() === 11 ? agora.getFullYear() + 1 : agora.getFullYear(),
  });
  const [copiarPreview, setCopiarPreview] = useState(null);
  const [copiarLoading, setCopiarLoading] = useState(false);

  // Modal Importar
  const [dialogImportar, setDialogImportar] = useState(false);
  const [importStep, setImportStep] = useState(0);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importAcaoDuplicados, setImportAcaoDuplicados] = useState('ignorar');
  const [importArquivo, setImportArquivo] = useState(null);

  // Modal confirmação exclusão
  const [dialogExcluir, setDialogExcluir] = useState(false);
  const [metaParaExcluir, setMetaParaExcluir] = useState(null);

  const mostrarSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const fetchMetas = useCallback(async (page = 1, ord = ordenacao) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 50,
        order_by: ord.order_by,
        order_direction: ord.order_direction,
      };
      if (filtros.mes && filtros.ano)
        params.competencia = `${filtros.ano}-${String(filtros.mes).padStart(2, '0')}`;
      if (filtros.responsavel) params.responsavel = filtros.responsavel;
      if (filtros.seguradora) params.seguradora = filtros.seguradora;
      if (filtros.tipo_meta) params.tipo_meta = filtros.tipo_meta;

      const res = await api.get('/metas', { params });
      setMetas(res.data.dados);
      setPaginacao(res.data.paginacao);
    } catch (e) {
      mostrarSnackbar('Erro ao carregar metas', 'error');
    } finally {
      setLoading(false);
    }
  }, [filtros, ordenacao]);

  const fetchResponsaveis = useCallback(async () => {
    try {
      const res = await api.get('/responsaveis');
      setResponsaveis(res.data.responsaveis || res.data || []);
    } catch { }
  }, []);

  const fetchSeguradoras = useCallback(async () => {
    try {
      const res = await api.get('/seguradoras');
      setSeguradoras(res.data.data || res.data || []);
    } catch { }
  }, []);

  useEffect(() => {
    fetchMetas();
    fetchResponsaveis();
    fetchSeguradoras();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = (campo) => {
    const novo = {
      order_by: campo,
      order_direction: ordenacao.order_by === campo && ordenacao.order_direction === 'asc' ? 'desc' : 'asc',
    };
    setOrdenacao(novo);
    fetchMetas(1, novo);
  };

  const SortIcon = ({ campo }) => {
    if (ordenacao.order_by !== campo) return null;
    return ordenacao.order_direction === 'asc' ? <ArrowUpIcon fontSize="small" sx={{ ml: 0.5 }} /> : <ArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />;
  };

  // ---- CRUD ----
  const abrirCriar = () => {
    setEditando(null);
    setFormData({
      id_Responsavel: '', id_seguradora: '', mes: agora.getMonth() + 1,
      ano: agora.getFullYear(), valor: '', quantidade: '',
    });
    setFormErro('');
    setDialogAberto(true);
  };

  const abrirEditar = (meta) => {
    setEditando(meta);
    const [ano, mes] = meta.competencia.split('-');
    setFormData({
      id_Responsavel: meta.id_Responsavel,
      id_seguradora: meta.id_seguradora,
      mes: parseInt(mes),
      ano: parseInt(ano),
      valor: meta.valor > 0 ? meta.valor : '',
      quantidade: meta.quantidade !== null ? meta.quantidade : '',
    });
    setFormErro('');
    setDialogAberto(true);
  };

  const handleSeguradoraChange = (id_seg) => {
    setFormData(prev => ({ ...prev, id_seguradora: id_seg }));
  };

  const handleSubmitMeta = async () => {
    setFormErro('');
    const payload = {
      id_Responsavel: parseInt(formData.id_Responsavel),
      id_seguradora: parseInt(formData.id_seguradora),
      mes: parseInt(formData.mes),
      ano: parseInt(formData.ano),
      valor: formData.valor !== '' ? parseFloat(formData.valor) : 0,
      quantidade: formData.quantidade !== '' ? parseInt(formData.quantidade) : null,
    };

    if (!payload.id_Responsavel || !payload.id_seguradora) {
      setFormErro('Responsável e Seguradora são obrigatórios');
      return;
    }

    try {
      if (editando) {
        await api.put(`/metas/${editando.id}`, payload);
        mostrarSnackbar('Meta atualizada com sucesso');
      } else {
        await api.post('/metas', payload);
        mostrarSnackbar('Meta criada com sucesso');
      }
      setDialogAberto(false);
      fetchMetas();
    } catch (e) {
      const msg = e.response?.data?.message || 'Erro ao salvar meta';
      setFormErro(msg);
    }
  };

  const confirmarExclusao = (meta) => {
    setMetaParaExcluir(meta);
    setDialogExcluir(true);
  };

  const handleExcluir = async () => {
    try {
      await api.delete(`/metas/${metaParaExcluir.id}`);
      mostrarSnackbar('Meta excluída com sucesso');
      setDialogExcluir(false);
      fetchMetas();
    } catch (e) {
      mostrarSnackbar('Erro ao excluir meta', 'error');
    }
  };

  // ---- Copiar Mês ----
  const handlePrevisualizar = async () => {
    setCopiarLoading(true);
    setCopiarPreview(null);
    try {
      const res = await api.post('/metas/copiar-mes', {
        ...copiarForm, sobrescrever: false,
      });
      setCopiarPreview(res.data);
    } catch (e) {
      mostrarSnackbar('Erro ao pré-visualizar cópia', 'error');
    } finally {
      setCopiarLoading(false);
    }
  };

  const handleCopiarMes = async () => {
    setCopiarLoading(true);
    try {
      const res = await api.post('/metas/copiar-mes', copiarForm);
      mostrarSnackbar(`${res.data.copiadas} metas copiadas. ${res.data.ja_existiam} já existiam no destino.`);
      setDialogCopiar(false);
      setCopiarPreview(null);
      fetchMetas();
    } catch (e) {
      mostrarSnackbar('Erro ao copiar metas', 'error');
    } finally {
      setCopiarLoading(false);
    }
  };

  // ---- Download Template ----
  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/metas/download-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erro no download');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template_metas.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      mostrarSnackbar('Erro ao baixar template', 'error');
    }
  };

  // ---- Importar ----
  const handleUploadImport = async (arquivo) => {
    if (!arquivo) return;
    setImportArquivo(arquivo);
    setImportLoading(true);
    setImportPreview(null);
    try {
      const form = new FormData();
      form.append('file', arquivo);
      const res = await api.post('/metas/importar', form);
      setImportPreview(res.data);
      setImportStep(1);
    } catch (e) {
      mostrarSnackbar(e.response?.data?.message || 'Erro ao processar arquivo', 'error');
    } finally {
      setImportLoading(false);
    }
  };

  const handleConfirmarImport = async () => {
    setImportLoading(true);
    try {
      const res = await api.post('/metas/importar/confirmar', {
        dados_novos: importPreview.dados_novos,
        dados_duplicados: importPreview.dados_duplicados,
        acao_duplicados: importAcaoDuplicados,
      });
      mostrarSnackbar(
        `Importação concluída: ${res.data.inseridos} inseridas, ${res.data.atualizados} atualizadas, ${res.data.ignorados} ignoradas.`
      );
      setDialogImportar(false);
      setImportStep(0);
      setImportPreview(null);
      fetchMetas();
    } catch (e) {
      mostrarSnackbar('Erro ao confirmar importação', 'error');
    } finally {
      setImportLoading(false);
    }
  };

  const segSelecionada = seguradoras.find(s => s.id === formData.id_seguradora || s.id === parseInt(formData.id_seguradora));
  const tipoMetaSeg = segSelecionada?.tipo_meta_padrao || 'valor';
  const isValorDisabled = tipoMetaSeg === 'quantidade';
  const isQtdDisabled = tipoMetaSeg === 'valor';

  return (
    <Layout>
      <Box sx={{ p: 0 }}>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" fontWeight="bold">Cadastro de Metas</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={abrirCriar}>
              Nova Meta
            </Button>
            <Tooltip title="Baixar planilha modelo para preenchimento">
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>
                Baixar Modelo
              </Button>
            </Tooltip>
            <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => { setDialogImportar(true); setImportStep(0); setImportPreview(null); }}>
              Importar Planilha
            </Button>
            <Button variant="outlined" startIcon={<CopyIcon />} onClick={() => { setDialogCopiar(true); setCopiarPreview(null); }}>
              Copiar Mês
            </Button>
          </Box>
        </Box>

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterIcon color="action" />
            <Typography variant="subtitle2">Filtros</Typography>
          </Box>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Mês</InputLabel>
                <Select label="Mês" value={filtros.mes} onChange={e => setFiltros(f => ({ ...f, mes: e.target.value }))}>
                  {MESES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Ano</InputLabel>
                <Select label="Ano" value={filtros.ano} onChange={e => setFiltros(f => ({ ...f, ano: e.target.value }))}>
                  {ANOS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Autocomplete
                size="small"
                options={responsaveis}
                getOptionLabel={(option) => option.Responsavel || option.nome || ''}
                value={responsaveis.find(r => (r.Responsavel || r.nome) === filtros.responsavel) || null}
                onChange={(_, newVal) => setFiltros(f => ({ ...f, responsavel: newVal ? (newVal.Responsavel || newVal.nome) : '' }))}
                renderInput={(params) => <TextField {...params} label="Responsável" />}
                ListboxProps={{ style: { maxHeight: 200 } }}
                noOptionsText="Nenhum encontrado"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Autocomplete
                size="small"
                options={seguradoras}
                getOptionLabel={(option) => option.nome || ''}
                value={seguradoras.find(s => s.nome === filtros.seguradora) || null}
                onChange={(_, newVal) => setFiltros(f => ({ ...f, seguradora: newVal ? newVal.nome : '' }))}
                renderInput={(params) => <TextField {...params} label="Seguradora" />}
                ListboxProps={{ style: { maxHeight: 200 } }}
                noOptionsText="Nenhum encontrado"
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Meta</InputLabel>
                <Select label="Tipo Meta" value={filtros.tipo_meta} onChange={e => setFiltros(f => ({ ...f, tipo_meta: e.target.value }))}>
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="valor">Valor</MenuItem>
                  <MenuItem value="quantidade">Quantidade</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" size="small" onClick={() => fetchMetas(1)}>Filtrar</Button>
                <Button variant="outlined" size="small" onClick={() => {
                  setFiltros({ mes: agora.getMonth() + 1, ano: agora.getFullYear(), responsavel: '', seguradora: '', tipo_meta: '' });
                  setTimeout(() => fetchMetas(1), 0);
                }}>Limpar</Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Tabela */}
        <Paper>
          {loading && <LinearProgress />}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell sx={{ fontWeight: 'bold', cursor: 'pointer' }} onClick={() => handleSort('responsavel')}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>Responsável<SortIcon campo="responsavel" /></Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', cursor: 'pointer' }} onClick={() => handleSort('seguradora')}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>Seguradora<SortIcon campo="seguradora" /></Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', cursor: 'pointer' }} onClick={() => handleSort('competencia')}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>Competência<SortIcon campo="competencia" /></Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Tipo Meta</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', textAlign: 'right' }} onClick={() => handleSort('valor')}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>Meta Valor<SortIcon campo="valor" /></Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', cursor: 'pointer', textAlign: 'right' }} onClick={() => handleSort('quantidade')}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>Meta Qtd<SortIcon campo="quantidade" /></Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {metas.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nenhuma meta encontrada para os filtros selecionados.
                    </TableCell>
                  </TableRow>
                ) : metas.map(m => {
                  const chip = CHIP_TIPO_META[m.tipo_meta] || { label: m.tipo_meta, color: 'default' };
                  return (
                    <TableRow key={m.id} hover>
                      <TableCell>{m.responsavel_nome}</TableCell>
                      <TableCell>{m.seguradora_nome}</TableCell>
                      <TableCell>{m.competencia_formatada}</TableCell>
                      <TableCell>
                        <Chip label={chip.label} color={chip.color} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        {m.tipo_meta === 'quantidade' ? '—' : fmt(m.valor)}
                      </TableCell>
                      <TableCell align="right">
                        {m.quantidade !== null && m.quantidade !== undefined ? m.quantidade : '—'}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => abrirEditar(m)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" color="error" onClick={() => confirmarExclusao(m)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {paginacao.total_paginas > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {paginacao.total_registros} registros encontrados
              </Typography>
              <Pagination
                count={paginacao.total_paginas}
                page={paginacao.pagina_atual}
                onChange={(_, p) => fetchMetas(p)}
                color="primary"
                size="small"
              />
            </Box>
          )}
          {paginacao.total_paginas <= 1 && (
            <Box sx={{ p: 1, textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">
                {paginacao.total_registros} registros encontrados
              </Typography>
            </Box>
          )}
        </Paper>

        {/* ---- Modal Criar/Editar ---- */}
        <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{editando ? 'Editar Meta' : 'Nova Meta'}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  options={responsaveis}
                  getOptionLabel={(option) => option.Responsavel || option.nome || ''}
                  value={responsaveis.find(r => r.id === formData.id_Responsavel) || null}
                  onChange={(_, newVal) => setFormData(f => ({ ...f, id_Responsavel: newVal ? newVal.id : '' }))}
                  renderInput={(params) => <TextField {...params} label="Responsável" required />}
                  ListboxProps={{ style: { maxHeight: 200 } }}
                  noOptionsText="Nenhum encontrado"
                />
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  size="small"
                  options={seguradoras}
                  getOptionLabel={(option) => option.nome || ''}
                  value={seguradoras.find(s => s.id === formData.id_seguradora) || null}
                  onChange={(_, newVal) => handleSeguradoraChange(newVal ? newVal.id : '')}
                  renderInput={(params) => <TextField {...params} label="Seguradora" required />}
                  ListboxProps={{ style: { maxHeight: 200 } }}
                  noOptionsText="Nenhum encontrado"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Mês</InputLabel>
                  <Select label="Mês" value={formData.mes}
                    onChange={e => setFormData(f => ({ ...f, mes: e.target.value }))}>
                    {MESES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small" required>
                  <InputLabel>Ano</InputLabel>
                  <Select label="Ano" value={formData.ano}
                    onChange={e => setFormData(f => ({ ...f, ano: e.target.value }))}>
                    {ANOS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              {segSelecionada && (
                <Grid item xs={12}>
                  <Box sx={{ px: 1.5, py: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">Tipo de meta (configurado na seguradora)</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {tipoMetaSeg === 'quantidade' ? '📊 Quantidade — meta medida por contagem de Nova Cotação' : '💰 Valor — meta medida pela soma do Prêmio Líquido'}
                    </Typography>
                  </Box>
                </Grid>
              )}
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Meta de Valor (R$)"
                  type="number" inputProps={{ min: 0, step: '0.01' }}
                  value={isValorDisabled ? '' : formData.valor}
                  onChange={e => setFormData(f => ({ ...f, valor: e.target.value }))}
                  disabled={isValorDisabled}
                  placeholder={isValorDisabled ? 'N/A' : '0.00'}
                  required={!isValorDisabled}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth size="small" label="Meta de Quantidade"
                  type="number" inputProps={{ min: 0, step: 1 }}
                  value={isQtdDisabled ? '' : formData.quantidade}
                  onChange={e => setFormData(f => ({ ...f, quantidade: e.target.value }))}
                  disabled={isQtdDisabled}
                  placeholder={isQtdDisabled ? 'N/A' : '0'}
                  required={!isQtdDisabled}
                />
              </Grid>
            </Grid>
            {formErro && <Alert severity="error" sx={{ mt: 2 }}>{formErro}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogAberto(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSubmitMeta}>Salvar</Button>
          </DialogActions>
        </Dialog>

        {/* ---- Modal Excluir ---- */}
        <Dialog open={dialogExcluir} onClose={() => setDialogExcluir(false)}>
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <Typography>
              Excluir a meta de <strong>{metaParaExcluir?.responsavel_nome}</strong> —{' '}
              <strong>{metaParaExcluir?.seguradora_nome}</strong> ({metaParaExcluir?.competencia_formatada})?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogExcluir(false)}>Cancelar</Button>
            <Button variant="contained" color="error" onClick={handleExcluir}>Excluir</Button>
          </DialogActions>
        </Dialog>

        {/* ---- Modal Copiar Mês ---- */}
        <Dialog open={dialogCopiar} onClose={() => setDialogCopiar(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Copiar Metas de Mês</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}><Typography variant="subtitle2">Mês Origem</Typography></Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Mês</InputLabel>
                  <Select label="Mês" value={copiarForm.mes_origem}
                    onChange={e => setCopiarForm(f => ({ ...f, mes_origem: e.target.value }))}>
                    {MESES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ano</InputLabel>
                  <Select label="Ano" value={copiarForm.ano_origem}
                    onChange={e => setCopiarForm(f => ({ ...f, ano_origem: e.target.value }))}>
                    {ANOS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}><Divider /><Typography variant="subtitle2" sx={{ mt: 1 }}>Mês Destino</Typography></Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Mês</InputLabel>
                  <Select label="Mês" value={copiarForm.mes_destino}
                    onChange={e => setCopiarForm(f => ({ ...f, mes_destino: e.target.value }))}>
                    {MESES.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ano</InputLabel>
                  <Select label="Ano" value={copiarForm.ano_destino}
                    onChange={e => setCopiarForm(f => ({ ...f, ano_destino: e.target.value }))}>
                    {ANOS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button variant="outlined" size="small" onClick={handlePrevisualizar} disabled={copiarLoading}>
                  {copiarLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                  Pré-visualizar
                </Button>
              </Grid>
              {copiarPreview && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    <strong>{copiarPreview.total_origem}</strong> metas no mês origem.{' '}
                    <strong>{copiarPreview.copiadas + copiarPreview.ja_existiam}</strong> seriam copiadas,{' '}
                    <strong>{copiarPreview.ja_existiam}</strong> já existem no destino e serão ignoradas.
                  </Alert>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogCopiar(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleCopiarMes} disabled={copiarLoading}>
              {copiarLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Copiar Metas
            </Button>
          </DialogActions>
        </Dialog>

        {/* ---- Modal Importar ---- */}
        <Dialog open={dialogImportar} onClose={() => setDialogImportar(false)} maxWidth="md" fullWidth>
          <DialogTitle>Importar Planilha de Metas</DialogTitle>
          <DialogContent dividers>
            <Stepper activeStep={importStep} sx={{ mb: 3 }}>
              <Step><StepLabel>Upload</StepLabel></Step>
              <Step><StepLabel>Preview</StepLabel></Step>
            </Stepper>

            {importStep === 0 && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Baixe o modelo padrão antes de preencher a planilha.
                </Alert>
                <Box
                  sx={{
                    border: '2px dashed', borderColor: 'primary.main', borderRadius: 2,
                    p: 4, textAlign: 'center', cursor: 'pointer',
                    '&:hover': { bgcolor: 'primary.50' },
                  }}
                  onClick={() => document.getElementById('import-file-input').click()}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="body1">Clique para selecionar ou arraste o arquivo .xlsx</Typography>
                  {importArquivo && <Typography variant="caption" color="text.secondary">{importArquivo.name}</Typography>}
                  <input
                    id="import-file-input" type="file" accept=".xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={e => e.target.files[0] && handleUploadImport(e.target.files[0])}
                  />
                </Box>
                {importLoading && <LinearProgress sx={{ mt: 2 }} />}
              </Box>
            )}

            {importStep === 1 && importPreview && (
              <Box>
                {/* Resumo */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={4}>
                    <Card sx={{ bgcolor: '#e8f5e9' }}>
                      <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                        <Typography variant="h4" color="success.main">{importPreview.resumo.novas}</Typography>
                        <Typography variant="caption">Novas (serão inseridas)</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card sx={{ bgcolor: '#fff8e1' }}>
                      <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                        <Typography variant="h4" color="warning.main">{importPreview.resumo.duplicadas}</Typography>
                        <Typography variant="caption">Duplicadas</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={4}>
                    <Card sx={{ bgcolor: '#fce4ec' }}>
                      <CardContent sx={{ textAlign: 'center', py: 1.5 }}>
                        <Typography variant="h4" color="error.main">{importPreview.resumo.erros}</Typography>
                        <Typography variant="caption">Com Erro</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Duplicadas */}
                {importPreview.resumo.duplicadas > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Metas duplicadas — o que fazer?
                    </Typography>
                    <RadioGroup row value={importAcaoDuplicados} onChange={e => setImportAcaoDuplicados(e.target.value)}>
                      <FormControlLabel value="sobrescrever" control={<Radio />} label="Sobrescrever com os novos valores" />
                      <FormControlLabel value="ignorar" control={<Radio />} label="Ignorar (manter os atuais)" />
                    </RadioGroup>
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1, maxHeight: 200 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Linha</TableCell>
                            <TableCell>Responsável</TableCell>
                            <TableCell>Seguradora</TableCell>
                            <TableCell>Compet.</TableCell>
                            <TableCell>Valor atual → novo</TableCell>
                            <TableCell>Qtd atual → nova</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {importPreview.preview_duplicadas.map((d, i) => (
                            <TableRow key={i}>
                              <TableCell>{d.linha}</TableCell>
                              <TableCell>{d.responsavel}</TableCell>
                              <TableCell>{d.seguradora}</TableCell>
                              <TableCell>{d.competencia_formatada}</TableCell>
                              <TableCell>{fmt(d.valor_atual)} → {fmt(d.valor)}</TableCell>
                              <TableCell>{d.quantidade_atual ?? '—'} → {d.quantidade ?? '—'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {/* Erros */}
                {importPreview.resumo.erros > 0 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom color="error">
                      Linhas com erro (não serão importadas):
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 180 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Linha</TableCell>
                            <TableCell>Responsável</TableCell>
                            <TableCell>Seguradora</TableCell>
                            <TableCell>Motivo</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {importPreview.erros.map((e, i) => (
                            <TableRow key={i}>
                              <TableCell>{e.linha}</TableCell>
                              <TableCell>{e.dados?.Responsavel}</TableCell>
                              <TableCell>{e.dados?.Seguradora}</TableCell>
                              <TableCell>{e.motivo}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setDialogImportar(false); setImportStep(0); setImportPreview(null); }}>
              Cancelar
            </Button>
            {importStep === 1 && (
              <Button
                variant="contained"
                onClick={handleConfirmarImport}
                disabled={importLoading || (importPreview?.resumo.novas === 0 && importPreview?.resumo.duplicadas === 0)}
              >
                {importLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                Confirmar Importação
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default MetasPanel;
